<script lang="ts" setup>
import { Button } from '@/components/global'
import { PlusIcon } from '@/components/svg'

import type { ListMember } from '@/types'

const props = withDefaults(
  defineProps<{
    member: ListMember
    action?: 'add' | 'remove'
    loading?: boolean
  }>(),
  {
    action: 'remove',
    loading: false,
  }
)

const emit = defineEmits<{
  action: [member: ListMember]
}>()

const getInitials = (member: ListMember): string => {
  const first = member.firstname ?? member.firstName
  const last = member.lastname ?? member.lastName
  if (first && last) return (first[0] + last[0]).toUpperCase()
  if (member.name) {
    const parts = member.name.trim().split(/\s+/)
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
  }
  return member.email.slice(0, 2).toUpperCase()
}

const getDisplayName = (member: ListMember): string => {
  const first = member.firstname ?? member.firstName
  const last = member.lastname ?? member.lastName
  if (first || last) return `${first ?? ''} ${last ?? ''}`.trim()
  return member.name ?? member.email
}
</script>

<template>
  <div
    class="d-flex justify-content-between align-items-center"
    :style="{ borderBottom: '0.45px solid #0000001a', padding: '0.35rem 0' }"
  >
    <div class="d-flex align-items-center gap-2">
      <div class="member-circle">{{ getInitials(member) }}</div>
      <div class="member-details d-flex flex-column">
        <h1 class="mt-2">{{ getDisplayName(member) }}</h1>
        <span class="mb-2">{{ member.email }}</span>
      </div>
    </div>

    <div class="d-flex justify-content-end" style="flex-shrink: 0;">
      <Button
        variant="naked"
        size="xs"
        :loading="loading"
        @click="emit('action', member)"
      >
        <div v-if="action === 'add'" class="action-pill add-pill px-2 d-flex align-items-center gap-1">
          <PlusIcon style="width: 0.625rem; height: 0.625rem; flex-shrink: 0;" />
          <span>Add</span>
        </div>
        <div v-else class="action-pill remove-pill px-2">
          <span>Remove</span>
        </div>
      </Button>
    </div>
  </div>
</template>

<style scoped>
.member-circle {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 100%;
  background-color: var(--ch-light-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-base);
  font-weight: 500;
  flex-shrink: 0;
}

.member-details h1 {
  font-weight: 400;
  font-size: var(--font-sm);
  line-height: 100%;
  letter-spacing: -1%;
}

.member-details span {
  font-size: var(--font-xs);
  color: var(--ch-black-40);
  font-weight: 400;
  line-height: 100%;
  letter-spacing: -3%;
}

.action-pill {
  border-radius: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.action-pill span {
  font-weight: 500;
  font-size: var(--font-xs);
  line-height: 160%;
  letter-spacing: 0.2px;
}

.remove-pill {
  background-color: var(--ch-pill);
  color: var(--ch-warning);
}

.add-pill {
  background-color: #e9faef;
  color: var(--ch-active);
}
</style>
